export default class YoutubePlayer {
    static init() {
        Array.prototype.forEach.call(document.querySelectorAll('.youtube-player'), el => {
            new YoutubePlayer(el);
        });
    }

    // Lazy: inject iframe_api only when the user clicks the consent overlay.
    // _scriptStatus: 'pending' | 'loaded' | undefined (failed/never tried). Set on onload, cleared on onerror.
    static loadScript() {
        if (YoutubePlayer._scriptStatus === 'pending' || YoutubePlayer._scriptStatus === 'loaded') return;
        if (window.YT && window.YT.Player) {
            YoutubePlayer._scriptStatus = 'loaded';
            return;
        }
        YoutubePlayer._scriptStatus = 'pending';

        let tag = document.createElement('script');
        let firstScriptTag = document.getElementsByTagName('script')[0];

        tag.src = 'https://www.youtube.com/iframe_api';
        tag.onload = () => {
            // window.YT.Player only populates after the secondary www-widgetapi.js loads; leave pending if not yet ready.
            if (window.YT && window.YT.Player) {
                YoutubePlayer._scriptStatus = 'loaded';
            }
        };
        tag.onerror = () => {
            YoutubePlayer._scriptStatus = undefined;
        };
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

	static classes = {
		node: 'youtube-player',
		get overlay() {
			return this.node + '__overlay'
		},
		get iframe() {
			return this.node + '__iframe'
		},
		state: {
			get initialised() {
				return YoutubePlayer.classes.node + '--is-initialized'
			},
			get unstarted() {
				return YoutubePlayer.classes.node + '--is-unstarted'
			},
			get ended() {
				return YoutubePlayer.classes.node + '--is-ended'
			},
			get playing() {
				return YoutubePlayer.classes.node + '--is-playing'
			},
			get paused() {
				return YoutubePlayer.classes.node + '--is-paused'
			},
			get buffering() {
				return YoutubePlayer.classes.node + '--is-buffering'
			},
			get cued() {
				return YoutubePlayer.classes.node + '--is-cued'
			}
		}
	}

	constructor(element) {
		this.options = '';
		this.element = element;
		this.iframe = element.querySelector('.' + YoutubePlayer.classes.iframe);
		this.overlay = element.querySelector('.' + YoutubePlayer.classes.overlay);
		this.state = 'unstarted';
		this.isInitialised = false;
		this.isInitialising = false;

		this.overlay.addEventListener('click', () => this.startPlayer());
	}

    // Polls window.YT.Player until ready; calls onTimeout() after the budget so callers can retry.
    onPlayerApiReady(fn, onTimeout, deadline) {
        if (deadline === undefined) {
            // 30s budget — covers slow networks but short enough that a frustrated user retry-clicks rather than waits.
            deadline = Date.now() + 30000;
        }
        if (window.YT && window.YT.Player) {
            fn();
            return;
        }
        if (Date.now() > deadline) {
            if (typeof onTimeout === 'function') onTimeout();
            return;
        }
        setTimeout(() => this.onPlayerApiReady(fn, onTimeout, deadline), 100);
    }

    startPlayer() {
        if (this.isInitialised) {
            this.YTPlay();
            return;
        }
        if (this.isInitialising) return;
        this.isInitialising = true;

        YoutubePlayer.loadScript();
        this.onPlayerApiReady(
            () => {
                try {
                    let id = this.iframe.dataset.id;
                    // SDP register — best-effort: if uc-block.bundle.js never loaded
                    // (blocked / offline), continue without it so the player still works.
                    window.uc?.blockElements?.({
                        'BJz7qNsdj-7': ('#' + id), // Show youtube overlay over the element with ID "'#id'"
                    });
                    // Order matters: createPlayer first so setState's 'initialised' event
                    // carries detail.player; only flip isInitialised if createPlayer succeeded
                    // so a throw leaves the instance retry-able.
                    this.player = this.createPlayer(this.iframe);
                    this.isInitialised = true;
                    this.setState('initialised');
                } finally {
                    this.isInitialising = false;
                }
            },
            () => {
                // Mirror tag.onerror: clear _scriptStatus so the next click can re-inject iframe_api.
                YoutubePlayer._scriptStatus = undefined;
                this.isInitialising = false;
            }
        );
    }

	createPlayer(playerNode) {
		let options = {
			videoId: this.iframe.dataset.id,
			origin: this.iframe.dataset.origin,
			hl: this.iframe.dataset.language,
			iv_load_policy: 3,
			modestbranding: 1,
			showinfo: 0,
			rel: 0,
			enablejsapi: 1,
			playsinline: 1,
			autohide: 1,
			controls: 0,
			playlist: this.iframe.dataset.id
		}

		return new YT.Player(playerNode, {
			events: {
				'onReady': () => {
					this.YTPlay();
				},
				'onStateChange': (event) => {
					switch (event.data) {
						case -1:
							this.setState('unstarted');
							break;
						case 0:
							this.setState('ended');
							break;
						case 1:
							this.setState('playing');
							break;
						case 2:
							this.setState('paused');
							break;
						case 3:
							this.setState('buffering');
							break;
						case 5:
							this.setState('cued');
							break;
					}
				}
			},
			...options
		});
	}

	setState(state) {
		let stateClass = YoutubePlayer.classes.state[state];
		if (this.element.classList.contains(stateClass)) return;

        const event = new CustomEvent(state, {
            bubbles: false,
            detail: { player: this.player },
        });

        this.element.dispatchEvent(event);

		for (let key in YoutubePlayer.classes.state) {
			if (key !== 'initialised') {
				this.element.classList.remove(YoutubePlayer.classes.state[key]);
			}
		}
		this.state = state;
		this.element.classList.add(stateClass);
	}

	YTPlay() {
		this.player.playVideo();
		this.setState('playing');
	}

	YTPause() {
		this.player.pauseVideo();
		this.setState('paused');
	}

	YTStop() {
		this.player.stopVideo();
		this.setState('stopped');
	}
}

YoutubePlayer.init();
